// src/components/AudioRecorder/AudioPlayer.js
// Reusable audio player component for playing audio from URLs or blobs
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';

/**
 * AudioPlayer - A reusable audio player component
 *
 * @param {string} src - Audio source URL (can be blob URL or signed GCS URL)
 * @param {string} duration - Pre-calculated duration string (e.g., "1:30") - used as fallback
 * @param {string} fileSize - File size display string (e.g., "18.7 KB")
 * @param {boolean} loading - Show loading state while fetching audio URL
 * @param {boolean} compact - Use compact styling for inline display
 * @param {function} onError - Callback when audio fails to load/play
 */
// eslint-disable-next-line max-lines-per-function
const AudioPlayer = ({
  src,
  duration: durationProp = '0:00',
  fileSize,
  loading = false,
  compact = false,
  onError,
}) => {
  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [audioElement, setAudioElement] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Callback ref for audio element
  const audioRef = useCallback((node) => {
    if (node !== null) {
      setAudioElement(node);
    }
  }, []);

  // Format time helper
  const formatTime = useCallback((seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Parse duration from prop (format: "M:SS") to seconds
  const durationInSeconds = useMemo(() => {
    if (!durationProp) return 0;
    const parts = durationProp.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    }
    return 0;
  }, [durationProp]);

  // Audio event listeners
  useEffect(() => {
    if (!audioElement || !src) {
      return;
    }

    // Force load the audio when src changes
    audioElement.load();

    // Get duration - prefer audio element's duration, fall back to prop
    const getAudioDuration = () => {
      if (audioElement.duration && !isNaN(audioElement.duration) && isFinite(audioElement.duration)) {
        return audioElement.duration;
      }
      return durationInSeconds || 1;
    };

    const handleTimeUpdate = () => {
      const audioDuration = getAudioDuration();
      const progress = (audioElement.currentTime / audioDuration) * 100;
      setPlaybackProgress(Math.min(progress, 100));
      setCurrentTime(formatTime(audioElement.currentTime));
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setPlaybackProgress(100);
      setCurrentTime(formatTime(getAudioDuration()));
    };

    const handleLoadedMetadata = () => {
      setPlaybackProgress(0);
      setCurrentTime('0:00');
      setAudioError(null);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
      const audioDuration = getAudioDuration();
      const progress = (audioElement.currentTime / audioDuration) * 100;
      setPlaybackProgress(Math.min(progress, 100));
      setCurrentTime(formatTime(audioElement.currentTime));
    };

    const handleError = (e) => {
      const error = audioElement.error;
      console.error('Audio error event:', e);
      console.error('Audio element error:', error);
      console.error('Audio error code:', error?.code, 'message:', error?.message);
      console.error('Audio src:', audioElement.src?.substring(0, 100));

      // MediaError codes: 1=ABORTED, 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED
      let errorMessage = 'Failed to load audio';
      if (error) {
        switch (error.code) {
          case 1: errorMessage = 'Audio loading was aborted'; break;
          case 2: errorMessage = 'Network error while loading audio'; break;
          case 3: errorMessage = 'Audio format not supported or corrupted'; break;
          case 4: errorMessage = 'Audio source not supported'; break;
          default: errorMessage = `Audio error: ${error.message || 'Unknown error'}`;
        }
      }
      setAudioError(errorMessage);
      onError?.(error);
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);
    audioElement.addEventListener('error', handleError);

    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
      audioElement.removeEventListener('error', handleError);
    };
  }, [audioElement, src, formatTime, durationInSeconds, onError]);

  // Reset state when src changes
  useEffect(() => {
    setPlaybackProgress(0);
    setCurrentTime('0:00');
    setIsPlaying(false);
    setAudioError(null);
  }, [src]);

  const handlePlayPause = () => {
    if (!audioElement) {
      console.error('No audio element');
      return;
    }

    if (audioElement.paused) {
      audioElement.play().catch(err => {
        console.error('Failed to play audio:', err);
        setAudioError('Failed to play audio');
        onError?.(err);
      });
    } else {
      audioElement.pause();
    }
  };

  const handlePlaybackSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioElement) {
      audioElement.playbackRate = speed;
    }
  };

  const handleSeek = (e) => {
    if (!audioElement) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;

    const audioDuration = audioElement.duration && !isNaN(audioElement.duration) && isFinite(audioElement.duration)
      ? audioElement.duration
      : durationInSeconds || 1;

    const newTime = (percentage / 100) * audioDuration;
    audioElement.currentTime = newTime;
    setPlaybackProgress(percentage);
    setCurrentTime(formatTime(newTime));
  };

  // Loading state
  if (loading) {
    return (
      <Paper
        sx={{
          p: 2,
          bgcolor: '#f8f9fa',
          borderRadius: compact ? 2 : 3,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading audio...
        </Typography>
      </Paper>
    );
  }

  // Error state
  if (audioError && !src) {
    return (
      <Paper
        sx={{
          p: 2,
          bgcolor: '#fff5f5',
          borderRadius: compact ? 2 : 3,
          border: '1px solid',
          borderColor: 'error.light',
        }}
      >
        <Typography variant="body2" color="error">
          {audioError}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: compact ? 1.5 : 2,
        bgcolor: '#f8f9fa',
        borderRadius: compact ? 2 : 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" style={{ display: 'none' }} />

      {/* Progress Bar */}
      <Box
        sx={{
          position: 'relative',
          height: compact ? 6 : 8,
          mb: compact ? 1.5 : 2,
          cursor: src ? 'pointer' : 'default',
          borderRadius: 4,
          bgcolor: '#e0e0e0',
          overflow: 'hidden',
        }}
        onClick={src ? handleSeek : undefined}
      >
        {/* Progress fill */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${playbackProgress}%`,
            bgcolor: 'primary.main',
            borderRadius: 4,
            transition: isPlaying ? 'none' : 'width 0.1s ease',
          }}
        />
        {/* Playhead */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: `${playbackProgress}%`,
            width: compact ? 12 : 16,
            height: compact ? 12 : 16,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            transition: isPlaying ? 'none' : 'left 0.1s ease',
          }}
        />
      </Box>

      {/* Controls Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1 : 1.5 }}>
        {/* Play/Pause Button */}
        <IconButton
          onClick={handlePlayPause}
          disabled={!src || loading}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            width: compact ? 36 : 44,
            height: compact ? 36 : 44,
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&.Mui-disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            },
          }}
        >
          {isPlaying ? <PauseIcon fontSize={compact ? 'small' : 'medium'} /> : <PlayIcon fontSize={compact ? 'small' : 'medium'} />}
        </IconButton>

        {/* Time Display */}
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            color: 'text.primary',
            fontWeight: 500,
            minWidth: compact ? 65 : 75,
            fontSize: compact ? '0.75rem' : '0.875rem',
          }}
        >
          {currentTime} / {durationProp}
        </Typography>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Speed Controls */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[1, 1.5, 2].map((speed) => (
            <Chip
              key={speed}
              label={`${speed}x`}
              size="small"
              onClick={() => handlePlaybackSpeedChange(speed)}
              sx={{
                height: compact ? 22 : 26,
                minWidth: compact ? 32 : 38,
                bgcolor: playbackSpeed === speed ? 'primary.main' : 'grey.200',
                color: playbackSpeed === speed ? 'white' : 'text.secondary',
                fontWeight: 500,
                fontSize: compact ? '0.65rem' : '0.75rem',
                '&:hover': {
                  bgcolor: playbackSpeed === speed ? 'primary.dark' : 'grey.300',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* File info */}
      {fileSize && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
          {fileSize}
        </Typography>
      )}

      {/* Error display */}
      {audioError && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {audioError}
        </Typography>
      )}
    </Paper>
  );
};

export default AudioPlayer;
